import type { Server } from "@cloudserver/contracts";
import { useNavigate } from "@tanstack/react-router";
import { MonitorPlay, SquareTerminal } from "lucide-react";
import { useTranslation } from "react-i18next";
import { openConsole } from "../pages/server/console/connection.js";
import { Button } from "../ui/primitives.js";
import { KindIcon } from "../ui/session.js";
import { connectionOf } from "./connections.js";
import { offerText, typeInTerminal, withoutEnter } from "./prefill.js";
import {
  insertTargets,
  kindOf,
  sessionTitle,
  terminals,
  useTerminals,
  type TerminalTab,
} from "./store.js";

const WORKSPACE = "/terminals";

/**
 * Puts a command into an open session and shows it: a terminal pastes it at
 * the prompt, a console or remote desktop opens «Набрать текст» with it.
 * Never with Enter.
 */
export function insertInto(tab: TerminalTab, text: string): void {
  terminals.activate(tab.id);
  if (kindOf(tab) === "ssh") {
    const connection = connectionOf(tab.id);
    connection?.paste(withoutEnter(text));
    requestAnimationFrame(() => connection?.focus());
  } else {
    offerText(tab.id, text);
  }
}

/**
 * Where a command another part of the panel suggests goes: «Вставить в
 * текущую сессию <name>» when a session of the server is open and connected
 * (the focused one first, terminals before consoles and desktops), with
 * opening a new one as the alternative; only the new one when none is open.
 * `open` is the new session's kind: a terminal (the assistant's shell
 * commands) or the console (commands for a server the terminal cannot reach yet).
 */
export function SendToSession({
  projectId,
  server,
  text,
  open,
  label,
  onDone,
}: {
  projectId: string;
  /** The server; until it has loaded only the open sessions are offered. */
  server: Server | undefined;
  text: string;
  open: "terminal" | "console";
  /** The new session's button when nothing is open («Открыть в терминале», «Набрать в консоли»). */
  label: string;
  onDone?: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const state = useTerminals();
  const serverId = server?.id;
  const target = serverId ? insertTargets(state, serverId)[0] : undefined;
  const show = () => {
    onDone?.();
    void navigate({ to: WORKSPACE });
  };
  const openNew = () => {
    if (!server) return;
    if (open === "console") {
      const id = openConsole({
        projectId,
        server: {
          id: server.id,
          name: server.name,
          osFlavor: server.image?.osFlavor ?? null,
          osVersion: server.image?.osVersion ?? null,
        },
      });
      offerText(id, text);
      show();
    } else {
      typeInTerminal(server.id, text);
      terminals.openPicker({ projectId, server });
      onDone?.();
    }
  };
  // The console is one per server: when it is the open session, opening "a new one" is the same.
  const sameConsole = open === "console" && target && kindOf(target) === "vnc";
  return (
    <>
      {target && (
        <Button
          variant="secondary"
          icon={<KindIcon kind={kindOf(target)} />}
          title={t("sessions.insert.hint")}
          onClick={() => {
            insertInto(target, text);
            show();
          }}
        >
          {t("sessions.insert.into", { name: sessionTitle(target) })}
        </Button>
      )}
      {!sameConsole && (
        <Button
          variant="secondary"
          disabled={!server}
          icon={
            open === "console" ? (
              <MonitorPlay className="size-4" aria-hidden />
            ) : (
              <SquareTerminal className="size-4" aria-hidden />
            )
          }
          onClick={openNew}
        >
          {target
            ? t(open === "console" ? "sessions.insert.newConsole" : "sessions.insert.newTerminal")
            : label}
        </Button>
      )}
    </>
  );
}
